import { useCallback, useRef, useEffect } from 'react';

/**
 * A custom hook to manage event listener functions for use with an EventEmitter.
 * It ensures you always have access to the latest version of your listener function.
 *
 * @param {Function} listenerFn - The listener function you want to use with the EventEmitter.
 * @param {Array} deps - The dependency array for the listener function (similar to useCallback).
 * @returns {Function} - A stable function that can be registered with an EventEmitter.
 */
function useEmitterListener(listenerFn, deps) {
    // 1. Create a useCallback to track changes to the function based on dependencies.
    const memoizedListener = useCallback(listenerFn, deps);

    // 2. Put the result of the useCallback in a useRef to track the different versions.
    const listenerRef = useRef(memoizedListener);

    // Update the ref whenever the memoizedListener changes
    useEffect(() => {
        listenerRef.current = memoizedListener;
    }, [memoizedListener]);

    // 3. Return a function that can be registered in the emitter and always use the latest version of it that is on useRef.
    // 4. Return a function that can be registered in the emitter and always use the latest version of it that is on useRef.
    // Return a stable function that, when called, executes the latest listener function.
    return useCallback(
        (...args) => {
            // Access the latest listener function from the ref and call it.
            listenerRef.current(...args);
        },
        [listenerRef] // stableListener only changes if listenerRef changes. But listenerRef never changes.
    );
}

export default useEmitterListener;